import React, { useEffect, useState, useMemo } from 'react';
import { AlertUnsavedData, Modal, ModalSize, NotificationToaster } from 'components/notifications';
import { Tabs, InternalLink } from 'components/navigation';
import { AssetService, FleetService, LinkService } from 'services';
import PropTypes from "prop-types";
import { Intent, Position } from '@blueprintjs/core';
import { AssetStatus } from 'components/status/AssetStatus';
import { ShowHide } from 'components/layout';
import { FormDatePeriodSelector, FormHeading } from 'components/form-fields';
import moment from 'moment';
import { Tooltip } from "components/tooltip";
import { FleetDayDetailEventsTab } from 'pages/fleet/fleet-day-detail/tabs/FleetDayDetailActivitiesTab';
import { NotesTab } from 'pages/vehicle/day-detail-modal/notes-tab';
import { useDirtyData } from 'hooks/useDirtyData';
import { EventList } from 'components/events';
import { Button } from 'components/buttons';
import { EventSchedules } from 'pages/asset/events';
import { AssetHelperFunctions } from 'helpers';

export function FleetDayDetailModal(
    {
        closeDayDetailModal = () => { },

        mappedAssetOptions = {
            ownershipAttributeMappings: [],
            assetStatusesMappings: []
        },

        accountEventTypes = [],
        eventOptions = {},
        onEventsChanged = () => { },
        assetDetails,
        periodDetails,
        showDayDetailModal,
        assetProfileArea = "MMF"
    }
) {
    const periodSelectorDateFormat = "DD MMM YYYY";
    const dateTimeFormat = "YYYY-MM-DDTHH:mm:ssZ";
    const notesDateTimeFormat = "YYYY-MM-DD";
    const eventsTabKey = "EVENTS";
    const notesTabKey = "NOTES";
    const oneDayLessThanAWeek = 6;
    const [dayDetailTitle, setDayDetailTitle] = useState(<></>)
    const eventsTab = { title: "Events", selected: true, key: eventsTabKey };
    const notesTab = { title: "Notes", selected: false, key: notesTabKey, icon: "" };
    const [selectedTab, setSelectedTab] = useState(eventsTabKey);
    const [tabs, setTabs] = useState([eventsTab]);

    const [scopedAssetEvents, setScopedAssetEvents] = useState([]);
    const [fetchingEvents, setFetchingEvents] = useState(false);
    const [eventDateRange, setEventDateRange] = useState({
        startDate: "",
        endDate: ""
    });

    const [note, setNote] = useState("");
    const [showAlert, setShowAlert] = useState(false);
    const [initialNoteValue, setInitialNoteValue] = useState(note);
    const [currentNoteValue, setCurrentNoteValue] = useState(note);
    const isNotesDataDirty = useDirtyData(initialNoteValue, currentNoteValue);

    const [isNotesLoading, setIsNotesLoading] = useState(true);

    const [showSchedules, setShowSchedules] = useState(false);

    useEffect(() => {
        setInitialNoteValue(note);
        setCurrentNoteValue(note);

        setDayDetailTabs();
    }, [note])

    useEffect(() => {
        if (!showDayDetailModal) {
            resetTabs();
        }
    }, [showDayDetailModal]);

    useEffect(() => {
        if (!showDayDetailModal) {
            return;
        }

        setDayDetailTabs(); 
        setDayDetailTitle(getDayDetailTitle(`${getDetailTitleText()} Detail`));
    }, [assetDetails, mappedAssetOptions, periodDetails, showDayDetailModal, note, isNotesLoading]);

    function getDayDetailTitle(titleText) {
        return (
            <div className="inline-items">
                <FormHeading headingLevel="h2">{titleText}</FormHeading>
                <AssetStatus
                    statusId={assetDetails?.assetLocation?.assetStatus?.id}
                    ownershipId={assetDetails?.assetLocation?.assetOwnership?.id}
                    isLoading={false}
                    statusAttributeMappings={mappedAssetOptions.assetStatusesMappings}
                    ownershipAttributeMappings={mappedAssetOptions.ownershipAttributeMappings}
                />
                <FormHeading headingLevel="h2" isLoading={false}>
                    <Tooltip position={Position.TOP} content={`Asset Profile`}>
                        <InternalLink link={LinkService.getVehicleProfileUrl(assetDetails.assetId, 'details', `area=${assetProfileArea}`)}>{assetDetails?.registration}</InternalLink>
                    </Tooltip>
                </FormHeading>
            </div>
        )
    }

    function setDayDetailTabs() {
        const existingActivitiesTab = tabs.find(t => t.key === eventsTabKey) || eventsTab;

        const tempTabs = [existingActivitiesTab];

        if (periodDetails.periodType === "day") {
            const existingNoteTab = tabs.find(t => t.key === notesTabKey) || notesTab;

            const tempNoteTab = { 
                ...existingNoteTab, 
                icon: note ? "comment" : ""
            };

            tempTabs.push(tempNoteTab);
        }

        setTabs(tempTabs);
    }

    useEffect(() => {
        if (assetDetails.assetId && periodDetails.startDate && showDayDetailModal) {
            setDatePeriods(periodDetails.startDate);
        }
    }, [assetDetails.assetId, periodDetails.startDate, periodDetails.endDate, showDayDetailModal])

    useEffect(() => {
        if (assetDetails.assetId && eventDateRange.startDate && showDayDetailModal) {
            callNoteForDayDetail();
        }
    }, [assetDetails.assetId, eventDateRange, showDayDetailModal])

    function setDatePeriods(periodStart) {
        const { start, end } = getPeriodBounds(periodDetails.periodType, periodStart);

        const dateRange = {
            startDate: start.format(dateTimeFormat),
            endDate: end.format(dateTimeFormat)
        };

        setEventDateRange(dateRange);
    }

    useEffect(() => {
        if (eventDateRange?.startDate) {
            refreshEventsForPeriod(eventDateRange);
        }
    }, [eventDateRange])


    function getDetailTitleText() {
        switch (periodDetails.periodType) { 
            case "day":
                return "Day";
            case "weeks":
                return "Week";
            case "months":
                return "Month";
            default:
                return "Day";
        }
    }

    function onTabClick(index) {
        setTabs(tabs.map(function (t, i) {
            return {
                ...t,
                selected: index === i
            }
        }));

        setSelectedTab(tabs[index].key);
    }

    function resetTabs() {
        setSelectedTab("ACTIVITIES");
        setNote("");
        setInitialNoteValue("");
        setCurrentNoteValue("");
        setDayDetailTabs();
    }

    function handleCloseModal() {
        if (isNotesDataDirty) {
            setShowAlert(true);
        }
        else {
            resetTabs();
            closeDayDetailModal();
        } 

    }

    function handleConfirmLeave() {
        setShowAlert(false);
        resetTabs();
        closeDayDetailModal();
    }

    function handleCancelLeave() {
        setShowAlert(false);
    }

    function getPeriodBounds(periodType, startLike) {
        const base = moment.utc(startLike);

        let start;
        let end;

        switch (periodType) {
            case 'day':
                start = moment.utc(base).startOf('day');
                end = moment.utc(start).endOf('day');
                break;
            case 'weeks':
                start = moment.utc(base).startOf('isoWeek');
                end = moment.utc(start).add(oneDayLessThanAWeek, 'day').endOf('day');
                break;
            case 'months':
                start = moment.utc(base).startOf('month');
                end = moment.utc(start).add(1, 'month').add(-1, 'day').endOf('day');
                break;
            default:
                start = moment.utc(base).startOf('day');
                end = moment.utc(start).add(1, 'day').endOf('day');
                break;
        }

        return { start, end };
    }

    function eventAltered(dateRange, reloadingFromChild = false) {
        handleEventChange();
        refreshEventsForPeriod(dateRange, reloadingFromChild);
    }

    function refreshEventsForPeriod(dateRange, reloadingFromChild = false) {
        // if we are reloading from the activities tab then we use the stored date range to reload the events.
        if (reloadingFromChild) {
            dateRange = eventDateRange;
        }

        if (
            (fetchingEvents && !reloadingFromChild) ||
            dateRange == null ||
            dateRange.startDate === "" ||
            dateRange.startDate == null ||
            dateRange.endDate === "" ||
            dateRange.endDate == null
        ) {
            return;
        }

        setFetchingEvents(true);

        let stDtUtc = moment(dateRange.startDate).toISOString();
        let endDtUtc = moment(dateRange.endDate).toISOString();

        FleetService.searchAssetEvents(
            stDtUtc,
            endDtUtc,
            [assetDetails.assetId],
            []
        ).then(eventData => {
            const allEvents = (eventData && eventData.length > 0) ? (eventData[0].events ?? []) : [];

            const filtered = allEvents.filter(ev => {
                if (!ev?.eventDate) return false;
                const d = moment.utc(ev.eventDate);
                return d.isSameOrAfter(stDtUtc) && d.isBefore(endDtUtc);
            });

            setScopedAssetEvents(filtered);
        })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, `Failed to fetch events. ${error}`);
            })
            .finally(() => {
                setFetchingEvents(false);
            });
    }

    function callNoteForDayDetail() {
        setIsNotesLoading(true);

        const noteDate = eventDateRange != null && eventDateRange.startDate !== "" 
            ? moment(eventDateRange.startDate).format(notesDateTimeFormat) 
            : moment(periodDetails.startDate).format(notesDateTimeFormat); 

        AssetService.getNote(assetDetails.assetId, noteDate)
            .then((response) => {
                const newNote = response && response.note ? response.note : "";

                setNote(newNote);
                setInitialNoteValue(newNote);
                setCurrentNoteValue(newNote);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, `Failed to get note`, false);
            })
            .finally(() => {
                setIsNotesLoading(false);
            });
    }

    function reloadForNotes(noteValue) {
        setInitialNoteValue(noteValue);
        setNote(noteValue);
        if (onEventsChanged) {
            onEventsChanged();
        }
    }

    function handleEventChange() {
        if (onEventsChanged) {
            onEventsChanged();
        }
    }

    function onManageEventsClicked() {
        setShowSchedules(true);
    }

    function handleCloseSchedulesModal() {
        setShowSchedules(false);
    }

    const isAssetInactive = useMemo(() => {
        return (AssetHelperFunctions.isAssetInactive(assetDetails?.assetLocation?.assetStatus?.id, mappedAssetOptions?.assetStatusesMappings))
    }, [assetDetails, mappedAssetOptions.assetStatusesMappings]);

    return (
        <Modal
            isOpen={showDayDetailModal}
            onClose={handleCloseModal}
            title={dayDetailTitle}
            icon="warning-sign"
            intent={Intent.DANGER}
            confirmButtonText="Dismiss"
            className="listing-table-scroll-vertical"
            updateStateOnClose={false}
            size={ModalSize.SUPERWIDE}
        >
            <div className='align-tabs-and-date'>
                <Tabs tabs={tabs} 
                    onClick={onTabClick} 
                    loading={fetchingEvents || isNotesLoading} 
                />

                <FormDatePeriodSelector
                    id="listing-period-new"
                    periodType={periodDetails.periodType}
                    periodsToShow={1}
                    startDate={eventDateRange.startDate}
                    onChange={setDatePeriods}
                    dateFormat={periodSelectorDateFormat}
                    showCalendar={periodDetails.periodType === "day"}
                    showText={false}
                    usePortal={false}
                    showResetButton={false}
                />
            </div>

            <ShowHide
                evaluator={selectedTab === eventsTabKey}
                show={(
                    <>
                        <ShowHide
                            evaluator={!isAssetInactive}
                            show={(
                                <div className="spacer-bottom">
                                    <EventList
                                        assetId={assetDetails.assetId}
                                        periodType={periodDetails.periodType}
                                        eventDate={moment(eventDateRange.startDate).format(notesDateTimeFormat)}
                                        onEventCreated={() => eventAltered(null, true)}
                                        popoverUsePortal={false}
                                        disabled={fetchingEvents || isNotesLoading}
                                        loading={fetchingEvents || isNotesLoading}
                                    />
                                    <Button text="Manage Schedules" intent="primary" onClick={onManageEventsClicked} disabled={fetchingEvents || isNotesLoading}
                                        loading={fetchingEvents || isNotesLoading}></Button>
                                </div>
                            )}
                        />

                        <FleetDayDetailEventsTab
                            assetDetails={assetDetails}
                            eventsForPeriod={scopedAssetEvents}
                            accountEventTypes={accountEventTypes}
                            eventOptions={eventOptions}
                            mappedAssetOptions={mappedAssetOptions}
                            onEventsChanged={() => eventAltered(null, true)}
                            fetchingEvents={fetchingEvents}
                        />
                    </>
                )}
            />

            <ShowHide
                evaluator={selectedTab === notesTabKey}
                show={(
                    <NotesTab
                        assetId={assetDetails.assetId}
                        date={moment(eventDateRange.startDate).format(notesDateTimeFormat)}
                        note={currentNoteValue}
                        setNote={setCurrentNoteValue}
                        isLoading={isNotesLoading}
                        reloadContainer={reloadForNotes}
                    />
                )}
            />

            {showAlert && (
                <AlertUnsavedData
                    isDirty={isNotesDataDirty}
                    isDirtySamePage={isNotesDataDirty}
                    onConfirmLeave={handleConfirmLeave}
                    onCancelLeave={handleCancelLeave}
                />
            )}

            <Modal
                isOpen={showSchedules}
                onClose={handleCloseSchedulesModal}
                title={getDayDetailTitle("Manage Schedules")}
                confirmButtonText="Dismiss"
                className="listing-table-scroll-vertical"
                updateStateOnClose={false}
                size={ModalSize.LARGE}
            >
                <EventSchedules
                    asset={assetDetails}
                    eventTypesChanged={() => eventAltered(null, true)}
                    pageState="PROFILE"
                    addScheduleDefaultDate={moment(eventDateRange.startDate).format(periodSelectorDateFormat)}
                    mappedAssetOptions={mappedAssetOptions}
                />
            </Modal>

        </Modal>
    )
}

FleetDayDetailModal.propTypes = {
    showDayDetailModal: PropTypes.bool.isRequired,
    periodDetails: PropTypes.exact({
        periodType: PropTypes.string,
        startDate: PropTypes.string
    }).isRequired,
    assetDetails: PropTypes.exact({
        assetId: PropTypes.number,
        assetLocation: PropTypes.exact({
            assetStatus: PropTypes.exact({ id: PropTypes.number }),
            assetOwnership: PropTypes.exact({ id: PropTypes.number }),
        }),
        registration: PropTypes.string
    }).isRequired,
    closeDayDetailModal: PropTypes.func,
    mappedAssetOptions: PropTypes.exact({
        ownershipAttributeMappings: PropTypes.array,
        assetStatusesMappings: PropTypes.array
    }),
    accountEventTypes: PropTypes.array,
    eventOptions: PropTypes.object,
    onEventsChanged: PropTypes.func,
    assetProfileArea: PropTypes.string
};